import type { Table as TableType } from "@tanstack/react-table";
import type { Virtualizer } from '@tanstack/react-virtual';
import clsx from "clsx";
import DefaultTableBodyContent from "components/Table/components/DefaultTableBodyContent";
import DefaultTableHeaderContent from "components/Table/components/DefaultTableHeaderContent";
import EmptyTableMessage from "components/Table/components/EmptyTableMessage";
import VirtualizedTableBodyContent from "components/Table/components/VirtualizedTableBodyContent";
import type { PropsWithChildren } from "react";

import DefaultTableFooterContent from "~/components/Table/components/DefaultTableFooterContent";
import Pagination from "~/components/Table/components/Pagination";
import TableBody from "~/components/Table/components/TableBody";
import TableBodyLoader from "~/components/Table/components/TableBodyLoader";
import TableFooter from "~/components/Table/components/TableFooter";
import TableHeader from "~/components/Table/components/TableHeader";
import TableWrapper from "~/components/Table/components/TableWrapper";
import type { TableHeaderBgTheme } from "~/components/Table/tableTypes.ts";

type TableProps<T> = PropsWithChildren & {
    disableUnselectableRows?: boolean;
    fixedWidth?: boolean;
    hasFilterBar?: boolean;
    headerBg?: TableHeaderBgTheme;
    horizontalBorders?: boolean;
    isLoading?: boolean;
    pagination?: boolean;
    rounded?: boolean;
    table: TableType<T>;
    usesRowSelection?: boolean;
    verticalBorders?: boolean;
    virtualized?: boolean;
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
    virtualizer?: Virtualizer<HTMLDivElement, Element>;
};

const Table = <T,>({
    children,
    disableUnselectableRows = false,
    fixedWidth,
    hasFilterBar = false,
    headerBg = "light",
    horizontalBorders = true,
    isLoading = false,
    pagination = false,
    rounded = true,
    table,
    verticalBorders = false,
    virtualized = false,
    scrollContainerRef,
    virtualizer,
}: TableProps<T>) => {
    const hasVisibleRows = table.getRowModel().rows.length > 0 || table.getFilteredRowModel()?.rows.length > 0;
    const hasFooters = table
        .getFooterGroups()
        .map((group) => group.headers.map((header) => header.column.columnDef.footer))
        .flat()
        .filter(Boolean);

    return (
        <TableWrapper
            rounded={rounded}
            className={clsx(fixedWidth && hasVisibleRows && "table-fixed")}
            scrollContainerRef={virtualized ? scrollContainerRef : undefined}
            virtualizer={virtualized ? virtualizer : undefined}
        >
            <TableHeader bgColor={headerBg} hasFilterBar={hasFilterBar}>
                {children}
                <DefaultTableHeaderContent rounded={rounded} table={table} />
            </TableHeader>
            <TableBody horizontalBorders={horizontalBorders} virtualized={virtualized} virtualizer={virtualizer}>
                {isLoading ? (
                    <TableBodyLoader />
                ) : (
                    <>
                        {virtualized && virtualizer ? (
                            <VirtualizedTableBodyContent
                                disableUnselectableRows={disableUnselectableRows}
                                table={table}
                                verticalBorders={verticalBorders}
                                virtualizer={virtualizer}
                                horizontalBorders={horizontalBorders}
                            />
                        ) : (
                            <DefaultTableBodyContent
                                disableUnselectableRows={disableUnselectableRows}
                                table={table}
                                verticalBorders={verticalBorders}
                            />
                        )}
                        <EmptyTableMessage table={table} />
                    </>
                )}
            </TableBody>
            {(hasFooters.length > 0 || (!virtualized && pagination)) && (
                <TableFooter>
                    {!virtualized && pagination ? (
                        <Pagination table={table} />
                    ) : hasFooters.length > 0 ? (
                        <DefaultTableFooterContent table={table} verticalBorders={verticalBorders} />
                    ) : null}
                </TableFooter>
            )}
        </TableWrapper>
    );
};

export default Table;